import { useRef, useState, useCallback } from 'react';

type SoundName = 'correct'|'wrong'|'tick'|'timeout'|'match'|'complete';

export const BGM_TRACKS = [
  { name: '學習輕音樂', notes: [523,659,784,880,784,659,523,440], wave: 'triangle' as OscillatorType, tempo: 480, decay: 0.42 },
  { name: '古典鋼琴',   notes: [], piano: true },
  { name: '電子節拍',   notes: [], beat: true },
  { name: '爵士風',     notes: [], jazz: true },
  { name: '自然森林',   notes: [], forest: true },
  { name: '歡快進行曲', notes: [784,784,880,784,698,659,698,784], wave: 'sawtooth' as OscillatorType, tempo: 320, decay: 0.28 },
  { name: '夜間冥想',   notes: [174,196,220,196,174,155,174,196], wave: 'sine' as OscillatorType, tempo: 1800, decay: 1.6 },
  { name: '復古 8-bit', notes: [523,523,784,523,440,392,330,294], wave: 'square' as OscillatorType, tempo: 280, decay: 0.22 },
  { name: '海浪白噪音', notes: [], noise: true },
];

export function setBgmTrack(idx: number) {
  localStorage.setItem('gsat_bgm_track', String(idx));
}

// 目前曲目索引（舊版的「關閉音樂」等超出範圍的值改回第一首）
export function getBgmTrack() {
  const i = parseInt(localStorage.getItem('gsat_bgm_track') || '0');
  return i >= 0 && i < BGM_TRACKS.length ? i : 0;
}

// 背景音樂開關；未設定過時沿用舊設定：舊的總靜音或選了「關閉音樂」（索引 9）視為關閉
const LS_BGM_ON = 'gsat_bgm_on';
const readBgmOn = () => {
  const v = localStorage.getItem(LS_BGM_ON);
  if (v !== null) return v === 'true';
  const oldMute = localStorage.getItem('gsat_mute') === 'true';
  const oldOff = localStorage.getItem('gsat_bgm_track') === '9';
  return !oldMute && !oldOff;
};

export function useSound() {
  const ctxRef        = useRef<AudioContext|null>(null);
  const masterGainRef = useRef<GainNode|null>(null);
  const bgmRunningRef = useRef(false);
  const bgmSessionRef = useRef(0);

  const [bgmOn, setBgmOnState] = useState<boolean>(readBgmOn);
  // muted：只控制音效（答對、答錯等提示音）；背景音樂由 bgmOn 控制
  const [muted, setMutedState] = useState<boolean>(()=>{
    try { return JSON.parse(localStorage.getItem('gsat_mute')||'false'); } catch { return false; }
  });

  const getCtx = useCallback(()=>{
    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
      masterGainRef.current = ctxRef.current.createGain();
      masterGainRef.current.connect(ctxRef.current.destination);
      masterGainRef.current.gain.value = 1;
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(()=>{});
    }
    return ctxRef.current;
  },[]);

  const playSound = useCallback((name: SoundName)=>{
    if (muted) return;
    const ctx = getCtx();
    const master = masterGainRef.current!;

    const playTone = (freq:number, start:number, dur:number, gain=0.3, type:OscillatorType='sine')=>{
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      g.gain.setValueAtTime(gain, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(g); g.connect(master);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };

    switch(name) {
      case 'correct':  playTone(523,0,0.1); playTone(659,0.1,0.15); break;
      case 'wrong':    playTone(220,0,0.1,0.3,'sawtooth'); playTone(180,0.1,0.15,0.3,'sawtooth'); break;
      case 'tick':     playTone(880,0,0.05,0.15,'square'); break;
      case 'timeout':  playTone(200,0,0.15,0.4,'sawtooth'); playTone(150,0.15,0.2,0.4,'sawtooth'); break;
      case 'match':    playTone(659,0,0.12,0.25); break;
      case 'complete': playTone(523,0,0.1); playTone(659,0.12,0.1); playTone(784,0.24,0.2); break;
    }
  },[muted, getCtx]);

  const setMuted = useCallback((m:boolean)=>{
    setMutedState(m);
    localStorage.setItem('gsat_mute', JSON.stringify(m));
  },[]);

  const startBgm = useCallback(()=>{
    if (!readBgmOn()) return;
    const track = BGM_TRACKS[getBgmTrack()];
    if (!track) return;

    const ctx = getCtx();
    const master = masterGainRef.current!;
    bgmRunningRef.current = true;
    const session = ++bgmSessionRef.current;

    const alive = () => bgmRunningRef.current && bgmSessionRef.current === session && readBgmOn();

    const note = (freq: number, time: number, dur: number, gain: number, type: OscillatorType = 'sine') => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, time);
      g.gain.linearRampToValueAtTime(gain, time + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, time + dur);
      osc.connect(g); g.connect(master);
      osc.start(time); osc.stop(time + dur + 0.05);
    };

    const noiseBuffer = () => {
      const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      return buf;
    };

    // ── 海浪白噪音 ───────────────────────────────────────────────
    if ((track as any).noise) {
      const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
      const filt = ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.frequency.value = 700; filt.Q.value = 0.8;
      const lfo = ctx.createOscillator(); lfo.frequency.value = 0.13; lfo.type = 'sine';
      const lfoG = ctx.createGain(); lfoG.gain.value = 0.15;
      const wg = ctx.createGain(); wg.gain.value = 0.25;
      lfo.connect(lfoG); lfoG.connect(wg.gain);
      src.connect(filt); filt.connect(wg); wg.connect(master);
      src.start(); lfo.start();
      const check = () => { if (!alive()) { try { src.stop(); lfo.stop(); } catch {} return; } setTimeout(check, 500); };
      setTimeout(check, 500);
      return;
    }

    // ── 爵士風 ───────────────────────────────────────────────────
    if ((track as any).jazz) {
      // ii-V-I-VI in C: Dm7 G7 Cmaj7 Am7
      // Shell voicings (3rd + 7th only — rootless jazz sound)
      const shellChords = [
        [175, 262],  // Dm7: F3 + C4
        [247, 349],  // G7:  B3 + F4
        [165, 247],  // Cmaj7: E3 + B3
        [220, 330],  // Am7:  A3 + G4
      ];
      // Walking bass lines per chord (4 notes each, stepwise + chromatic passing)
      const walkLines = [
        [147, 165, 175, 185],  // Dm7: D C E F# (approach G)
        [196, 220, 233, 220],  // G7:  G A Bb A
        [131, 165, 196, 220],  // Cmaj7: C E G A
        [110, 131, 147, 165],  // Am7:  A C D E
      ];
      const beat = 60 / 132;  // 132 BPM
      const swL = beat * 0.68; // swing long (2/3 of beat pair)
      const swS = beat * 0.32; // swing short
      // Ride cymbal hit (short high noise)
      const ride = (time: number, gain = 0.07) => {
        const src = ctx.createBufferSource(); src.buffer = noiseBuffer();
        const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 6000;
        const g = ctx.createGain();
        g.gain.setValueAtTime(gain, time); g.gain.exponentialRampToValueAtTime(0.001, time + 0.08);
        src.connect(hp); hp.connect(g); g.connect(master); src.start(time); src.stop(time + 0.1);
      };

      let barIdx = 0, t = ctx.currentTime + 0.05;
      const sched = () => {
        if (!alive()) { return; }
        while (t < ctx.currentTime + 3.5) {
          const chord = shellChords[barIdx % shellChords.length];
          const walk  = walkLines[barIdx % walkLines.length];
          // 4 beats per bar, each beat split swing long+short
          for (let b = 0; b < 4; b++) {
            const bt = t + b * beat;
            // Walking bass
            note(walk[b], bt, swL * 0.92, 0.14, 'sine');
            // Ride: every beat + triplet upbeat on beats 2 & 4 (ding-ding-da-ding)
            ride(bt, 0.09);
            if (b === 1 || b === 3) ride(bt + swL, 0.05); // "da" — triplet upbeat
            // Piano comp: syncopated — hit shell on beat 2 and "and-of-4"
            if (b === 1) chord.forEach(f => note(f, bt, swL * 1.2, 0.06, 'triangle'));
            if (b === 3) chord.forEach(f => note(f, bt + swL, swS * 1.5, 0.05, 'triangle'));
          }
          t += beat * 4; barIdx++;
        }
        setTimeout(sched, 1200);
      };
      sched(); return;
    }

    // ── 古典鋼琴（華爾滋 3/4，C大調 I-V-vi-IV）─────────────────
    if ((track as any).piano) {
      const beat = 0.55; // seconds per beat
      // bar: [bassNote, chord1, chord2] × 4 bars
      const bars = [
        { b: 131, c: [262,330,392] }, // C  Cmaj
        { b: 196, c: [294,392,494] }, // G  Gmaj
        { b: 220, c: [330,415,523] }, // A  Am
        { b: 175, c: [262,349,523] }, // F  Fmaj
      ];
      let barIdx = 0, t = ctx.currentTime + 0.05;
      const sched = () => {
        if (!alive()) { return; }
        while (t < ctx.currentTime + 3.0) {
          const bar = bars[barIdx % bars.length];
          const tBar = t;
          // Beat 1: bass + chord together
          note(bar.b, tBar, beat * 2.5, 0.12, 'triangle');
          bar.c.forEach(f => note(f, tBar + 0.02, beat * 2.2, 0.04, 'triangle'));
          // Beat 2: chord only
          bar.c.forEach(f => note(f, tBar + beat, beat * 0.9, 0.03, 'triangle'));
          // Beat 3: chord only
          bar.c.forEach(f => note(f, tBar + beat * 2, beat * 0.9, 0.03, 'triangle'));
          t += beat * 3; barIdx++;
        }
        setTimeout(sched, 1000);
      };
      sched(); return;
    }

    // ── 電子節拍（4/4, 120BPM）──────────────────────────────────
    if ((track as any).beat) {
      const bpm = 128, beat = 60 / bpm;
      const bassLine = [55,55,69,55,55,65,55,62]; // E1 pattern
      let bi = 0, t = ctx.currentTime + 0.05;

      const kick = (time: number) => {
        const osc = ctx.createOscillator(); const g = ctx.createGain();
        osc.frequency.setValueAtTime(150, time);
        osc.frequency.exponentialRampToValueAtTime(30, time + 0.15);
        g.gain.setValueAtTime(0.5, time); g.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
        osc.connect(g); g.connect(master); osc.start(time); osc.stop(time + 0.3);
      };
      const snare = (time: number) => {
        // noise burst
        const src = ctx.createBufferSource(); src.buffer = noiseBuffer();
        const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 1500;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.25, time); g.gain.exponentialRampToValueAtTime(0.001, time + 0.15);
        src.connect(filt); filt.connect(g); g.connect(master); src.start(time); src.stop(time + 0.18);
        // body tone
        const osc = ctx.createOscillator(); const g2 = ctx.createGain(); osc.type = 'triangle'; osc.frequency.value = 220;
        g2.gain.setValueAtTime(0.08, time); g2.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
        osc.connect(g2); g2.connect(master); osc.start(time); osc.stop(time + 0.12);
      };
      const hihat = (time: number, gain = 0.06) => {
        const src = ctx.createBufferSource(); src.buffer = noiseBuffer();
        const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 8000;
        const g = ctx.createGain();
        g.gain.setValueAtTime(gain, time); g.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
        src.connect(filt); filt.connect(g); g.connect(master); src.start(time); src.stop(time + 0.07);
      };

      const sched = () => {
        if (!alive()) { return; }
        while (t < ctx.currentTime + 2.0) {
          const sub = bi % 8; // 8 eighth-notes per 2 beats
          const beat4 = bi % 16; // position in 4/4 bar (16 eighth-notes)
          // Kick: beat 1 & 3 (positions 0, 8)
          if (beat4 === 0 || beat4 === 8) kick(t);
          // Snare: beat 2 & 4 (positions 4, 12)
          if (beat4 === 4 || beat4 === 12) snare(t);
          // Hi-hat: every 8th, open on offbeats
          hihat(t, sub % 2 === 0 ? 0.05 : 0.09);
          // Synth bass
          note(bassLine[bi % bassLine.length], t, beat * 0.8, 0.1, 'sawtooth');
          t += beat / 2; // eighth-note steps
          bi++;
        }
        setTimeout(sched, 600);
      };
      sched(); return;
    }

    // ── 自然森林（隨機鳥鳴 + 細微蟲鳴）──────────────────────────
    if ((track as any).forest) {
      const chirp = () => {
        if (!alive()) { return; }
        const t = ctx.currentTime;
        // Bird chirp: fast freq sweep
        const osc = ctx.createOscillator(); const g = ctx.createGain();
        const startF = 1200 + Math.random() * 800;
        const endF   = startF + (Math.random() > 0.5 ? 400 : -200);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(startF, t);
        osc.frequency.linearRampToValueAtTime(endF, t + 0.08);
        g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.07, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
        osc.connect(g); g.connect(master); osc.start(t); osc.stop(t + 0.15);
        // Occasional second chirp note
        if (Math.random() > 0.4) {
          const t2 = t + 0.14;
          const osc2 = ctx.createOscillator(); const g2 = ctx.createGain();
          osc2.type = 'sine'; osc2.frequency.value = endF * (Math.random() > 0.5 ? 1.33 : 0.75);
          g2.gain.setValueAtTime(0, t2); g2.gain.linearRampToValueAtTime(0.05, t2 + 0.01);
          g2.gain.exponentialRampToValueAtTime(0.001, t2 + 0.1);
          osc2.connect(g2); g2.connect(master); osc2.start(t2); osc2.stop(t2 + 0.12);
        }
        // Next chirp: 2-7 seconds
        setTimeout(chirp, (2 + Math.random() * 5) * 1000);
      };
      // Soft background wind (very low noise)
      const windBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const wd = windBuf.getChannelData(0);
      for (let i = 0; i < wd.length; i++) wd[i] = Math.random() * 2 - 1;
      const windSrc = ctx.createBufferSource(); windSrc.buffer = windBuf; windSrc.loop = true;
      const windF = ctx.createBiquadFilter(); windF.type = 'bandpass'; windF.frequency.value = 300; windF.Q.value = 0.3;
      const windG = ctx.createGain(); windG.gain.value = 0.04;
      windSrc.connect(windF); windF.connect(windG); windG.connect(master); windSrc.start();
      chirp();
      const checkWind = () => { if (!alive()) { try { windSrc.stop(); } catch {} return; } setTimeout(checkWind, 500); };
      setTimeout(checkWind, 500);
      return;
    }

    // ── 標準音符播放（學習輕音樂、歡快進行曲、夜間冥想、復古8-bit）──
    const activeNotes = track.notes;
    if (activeNotes.length === 0) return;
    const waveType = (track as any).wave as OscillatorType ?? 'sine';
    const tempoMs  = (track as any).tempo as number ?? 500;
    const decaySec = (track as any).decay as number ?? 0.4;
    let noteIdx = 0;

    const scheduleNote = () => {
      if (!alive()) { return; }
      const freq = activeNotes[noteIdx % activeNotes.length];
      noteIdx++;
      if (freq === 0) { setTimeout(scheduleNote, tempoMs); return; } // rest
      const osc = ctx.createOscillator(); const g = ctx.createGain();
      osc.type = waveType; osc.frequency.value = freq;
      g.gain.setValueAtTime(0.05, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decaySec);
      osc.connect(g); g.connect(master);
      osc.start(); osc.stop(ctx.currentTime + decaySec + 0.05);
      // 學習輕音樂加簡單和音（大三度）
      if (waveType === 'triangle') {
        const harm = freq * 1.25;
        const osc2 = ctx.createOscillator(); const g2 = ctx.createGain();
        osc2.type = 'triangle'; osc2.frequency.value = harm;
        g2.gain.setValueAtTime(0.02, ctx.currentTime); g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decaySec);
        osc2.connect(g2); g2.connect(master); osc2.start(); osc2.stop(ctx.currentTime + decaySec + 0.05);
      }
      setTimeout(scheduleNote, tempoMs);
    };
    scheduleNote();
  },[getCtx]);

  const stopBgm = useCallback(()=>{
    bgmRunningRef.current = false;
  },[]);

  const setBgmOn = useCallback((on:boolean)=>{
    setBgmOnState(on);
    localStorage.setItem(LS_BGM_ON, String(on));
    if (on) startBgm(); else bgmRunningRef.current = false;
  },[startBgm]);

  // 朗讀時降低背景音樂與音效音量
  const duck = useCallback((on: boolean) => {
    const g = masterGainRef.current, ctx = ctxRef.current;
    if (g && ctx) g.gain.setTargetAtTime(on ? 0.2 : 1, ctx.currentTime, 0.15);
  }, []);

  return { playSound, muted, setMuted, bgmOn, setBgmOn, startBgm, stopBgm, duck };
}
